import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  type Node,
  type Edge,
  type OnNodesChange,
  type OnEdgesChange,
  type OnConnect,
  type NodeMouseHandler,
  EdgeChange,
  NodeChange,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { BlockNode } from './BlockNode';
import { PropertiesPanel } from './PropertiesPanel';
import {
  BLOCK_TYPES,
  flowDefinitionToNodes,
  nodesToFlowDefinition,
  createNewNode,
} from './flowBuilderUtils';
import { validateFlow, type ValidationResult } from './flowValidation';
import type { FlowDefinition, FlowBlock } from '../../types/flow';
import { flowService } from '../../services/flow';

const nodeTypes = { blockNode: BlockNode };

interface FlowBuilderProps {
  flow: FlowDefinition | null;
  onSave: (flow: FlowDefinition) => void;
  onCancel: () => void;
}

export const FlowBuilder: React.FC<FlowBuilderProps> = (props) => {
  return (
    <ReactFlowProvider>
      <FlowBuilderInner {...props} />
    </ReactFlowProvider>
  );
};

const FlowBuilderInner: React.FC<FlowBuilderProps> = ({ flow, onSave, onCancel }) => {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [flowMeta, setFlowMeta] = useState({
    id: '',
    name: '',
    description: '',
    version: '1.0.0',
    enabled: true,
    entryNode: '',
  });
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [saving, setSaving] = useState(false);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const reactFlowInstance = useRef<any>(null);

  useEffect(() => {
    setValidation(null);
    setShowValidation(false);
    setSelectedNodeId(null);
    if (flow) {
      const { nodes: rfnodes, edges: rfedges } = flowDefinitionToNodes(flow);
      const nodesWithEntry = rfnodes.map((n) => ({
        ...n,
        data: { ...n.data, _isEntry: n.id === flow.entryNode },
      }));
      setNodes(nodesWithEntry);
      setEdges(rfedges);
      setFlowMeta({
        id: flow.id,
        name: flow.name,
        description: flow.description,
        version: flow.version,
        enabled: flow.enabled,
        entryNode: flow.entryNode,
      });
    } else {
      setNodes([]);
      setEdges([]);
      setFlowMeta({
        id: `flow-${Date.now().toString(36)}`,
        name: 'New Flow',
        description: '',
        version: '1.0.0',
        enabled: true,
        entryNode: '',
      });
    }
  }, [flow]);

  const onNodesChange: OnNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  );

  const onEdgesChange: OnEdgesChange = useCallback(
    (changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  );

  const onConnect: OnConnect = useCallback(
    (connection) => setEdges((eds) => addEdge({ ...connection, type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed } }, eds)),
    [],
  );

  const onNodeClick: NodeMouseHandler = useCallback((_evt, node) => {
    setSelectedNodeId(node.id);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const blockType = event.dataTransfer.getData('application/blocktype');
      if (!blockType) return;

      const position = reactFlowInstance.current
        ? reactFlowInstance.current.project({ x: event.clientX, y: event.clientY })
        : { x: event.clientX, y: event.clientY };

      const newNode = createNewNode(blockType, position);
      setNodes((nds) => [...nds, newNode]);
    },
    [],
  );

  const selectedNode = nodes.find((n) => n.id === selectedNodeId) || null;
  const selectedBlock: FlowBlock | null = selectedNode
    ? (() => { const d = { ...selectedNode.data }; delete (d as any)._meta; delete (d as any)._isEntry; return d as unknown as FlowBlock; })()
    : null;

  const updateBlock = useCallback((updated: FlowBlock) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === updated.id
          ? { ...n, data: { ...updated, _meta: n.data._meta, _isEntry: n.data._isEntry } }
          : n,
      ),
    );
  }, []);

  const deleteBlock = useCallback(() => {
    if (!selectedNodeId) return;
    setNodes((nds) => nds.filter((n) => n.id !== selectedNodeId));
    setEdges((eds) => eds.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId));
    if (flowMeta.entryNode === selectedNodeId) {
      setFlowMeta((m) => ({ ...m, entryNode: '' }));
    }
    setSelectedNodeId(null);
  }, [selectedNodeId, flowMeta.entryNode]);

  const setAsEntry = useCallback(() => {
    if (!selectedNodeId) return;
    setFlowMeta((m) => ({ ...m, entryNode: selectedNodeId }));
    setNodes((nds) =>
      nds.map((n) => ({ ...n, data: { ...n.data, _isEntry: n.id === selectedNodeId } })),
    );
  }, [selectedNodeId]);

  const handleValidate = useCallback(() => {
    const def = nodesToFlowDefinition(nodes, edges, flowMeta);
    const result = validateFlow(def);
    setValidation(result);
    setShowValidation(true);
  }, [nodes, edges, flowMeta]);

  const handleSave = useCallback(async () => {
    const def = nodesToFlowDefinition(nodes, edges, flowMeta);
    const result = validateFlow(def);
    setValidation(result);
    setShowValidation(true);

    if (!result.valid) return;

    setSaving(true);
    try {
      const existing = await flowService.getFlow(def.id);
      if (existing) {
        const updated = await flowService.updateFlow(def.id, def);
        if (updated) onSave(updated);
      } else {
        const created = await flowService.createFlow(def);
        if (created) onSave(created);
      }
    } catch (err: any) {
      console.error('[FlowBuilder] Save failed:', err.message);
    } finally {
      setSaving(false);
    }
  }, [nodes, edges, flowMeta, onSave]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col dark:bg-claude-darkBg bg-claude-bg">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b dark:border-claude-darkBorder border-claude-border shrink-0">
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={flowMeta.name}
            onChange={(e) => setFlowMeta((m) => ({ ...m, name: e.target.value }))}
            className="text-sm font-semibold bg-transparent border-b border-transparent focus:border-claude-accent outline-none dark:text-claude-darkText text-claude-text"
            placeholder="Flow name"
          />
          <input
            type="text"
            value={flowMeta.id}
            onChange={(e) => setFlowMeta((m) => ({ ...m, id: e.target.value }))}
            className="text-xs font-mono bg-transparent border-b border-transparent focus:border-claude-accent outline-none dark:text-claude-darkTextSecondary text-claude-textSecondary"
            placeholder="flow-id"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleValidate}
            className="text-xs px-3 py-1.5 rounded font-medium bg-gray-100 dark:bg-gray-800 dark:text-claude-darkText text-claude-text hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            Validate
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="text-xs px-3 py-1.5 rounded font-medium bg-claude-accent text-white hover:opacity-90 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save Flow'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="text-xs px-3 py-1.5 rounded font-medium bg-gray-100 dark:bg-gray-800 dark:text-claude-darkText text-claude-text hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Description bar */}
      <div className="px-4 py-1.5 border-b dark:border-claude-darkBorder/50 border-claude-border/50 shrink-0">
        <input
          type="text"
          value={flowMeta.description}
          onChange={(e) => setFlowMeta((m) => ({ ...m, description: e.target.value }))}
          className="w-full text-xs bg-transparent outline-none dark:text-claude-darkTextSecondary text-claude-textSecondary"
          placeholder="Flow description..."
        />
      </div>

      {/* Validation panel */}
      {showValidation && validation && (
        <div className={`px-4 py-2 border-b shrink-0 ${validation.valid ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}>
          {validation.errors.length > 0 && (
            <div className="text-xs text-red-600 dark:text-red-400 space-y-0.5">
              {validation.errors.map((e, i) => <div key={i}>✗ {e}</div>)}
            </div>
          )}
          {validation.warnings.length > 0 && (
            <div className="text-xs text-amber-600 dark:text-amber-400 space-y-0.5 mt-1">
              {validation.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
            </div>
          )}
          {validation.valid && validation.warnings.length === 0 && (
            <div className="text-xs text-green-600 dark:text-green-400">✓ Flow is valid</div>
          )}
        </div>
      )}

      {/* Main area: Palette + Canvas + Properties */}
      <div className="flex-1 flex overflow-hidden">
        {/* Block Palette */}
        <div className="w-48 shrink-0 border-r dark:border-claude-darkBorder border-claude-border p-2 space-y-1 overflow-y-auto">
          <p className="text-xs font-semibold uppercase tracking-wide dark:text-claude-darkTextSecondary text-claude-textSecondary px-1 pb-1">
            Blocks
          </p>
          {BLOCK_TYPES.map((bt) => (
            <div
              key={bt.type}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('application/blocktype', bt.type);
                e.dataTransfer.effectAllowed = 'move';
              }}
              className="flex items-center gap-2 px-2 py-2 rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-claude-surface cursor-grab hover:shadow-md transition-shadow"
            >
              <span className="text-base">{bt.icon}</span>
              <span className="text-xs font-medium dark:text-claude-darkText text-claude-text">{bt.label}</span>
            </div>
          ))}
          <div className="pt-2 mt-2 border-t dark:border-claude-darkBorder/50 border-claude-border/50">
            <p className="text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary px-1">
              Drag blocks onto the canvas. Click a block to edit properties.
            </p>
          </div>
        </div>

        {/* React Flow Canvas */}
        <div ref={reactFlowWrapper} className="flex-1 relative" onDrop={onDrop} onDragOver={onDragOver}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onInit={(inst) => { reactFlowInstance.current = inst; }}
            nodeTypes={nodeTypes}
            fitView
            className="dark:bg-claude-darkBg bg-claude-bg"
          >
            <Background gap={16} className="dark:opacity-20 opacity-40" />
            <Controls className="dark:!bg-claude-darkSurface !border-claude-border dark:!border-claude-darkBorder" />
            <MiniMap
              className="dark:!bg-claude-darkSurface"
              nodeColor={(n) => (n.data?._meta?.color as string) || '#6b7280'}
            />
          </ReactFlow>
        </div>

        {/* Properties Panel */}
        <div className="w-72 shrink-0 border-l dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-claude-surface">
          <PropertiesPanel
            block={selectedBlock}
            onChange={updateBlock}
            onDelete={deleteBlock}
            isEntryNode={selectedNodeId === flowMeta.entryNode}
            onSetEntry={setAsEntry}
          />
        </div>
      </div>
    </div>
  );
};